import { Injectable } from '@angular/core';
import { CustomComponentStore } from '@elio/shared/ngrx-utils';
import { CookieService } from 'ngx-cookie-service';
import { pipe, tap } from 'rxjs';

const COOKIE_CONSENT = 'cookieconsent';
const ACCEPT_ALL = 'allow';
const DENY_ALL = 'not-allowed';

const EXPIRE_DAYS = 365;
const COOKIE_PATH = '/';

export type CookieType = 'Google Analytics';

export interface CookieConsentState {
  display: boolean;
}

const initialState: CookieConsentState = {
  display: false,
};

@Injectable({
  providedIn: 'root',
})
export class CookieConsentStore extends CustomComponentStore<CookieConsentState> {
  public readonly getDisplayCookieConsent$ = this.select((state) => state.display);

  constructor(private cookieService: CookieService) {
    super(initialState);
  }

  readonly init = this.effect<void>(
    pipe(
      tap(() => {
        const cookieAlreadySet = this.cookieService.check(COOKIE_CONSENT);
        this.setDisplay(!cookieAlreadySet);
      })
    )
  );

  readonly acceptAllCookies = this.effect<void>(
    pipe(
      tap(() => {
        this.cookieService.set(COOKIE_CONSENT, ACCEPT_ALL, {
          expires: EXPIRE_DAYS,
          path: COOKIE_PATH,
        });
        this.setDisplay(false);
      })
    )
  );

  readonly refuseAllCookies = this.effect<void>(
    pipe(
      tap(() => {
        this.cookieService.set(COOKIE_CONSENT, DENY_ALL, {
          expires: EXPIRE_DAYS,
          path: COOKIE_PATH,
        });
        this.setDisplay(false);
      })
    )
  );

  readonly setCookie = this.effect<{ cookieType: CookieType; accept: boolean }>(
    pipe(
      tap(({ cookieType, accept }) => {
        const cookieAlreadySet = this.cookieService.check(COOKIE_CONSENT);
        const cookieConsent = this.cookieService.get(COOKIE_CONSENT);

        const defaultAcceptanceDetails = {
          'Google Analytics': false,
        };
        let previousAcceptanceDetails: Record<CookieType, boolean>;

        if (!cookieAlreadySet || cookieConsent === DENY_ALL) {
          previousAcceptanceDetails = { ...defaultAcceptanceDetails };
        } else if (cookieConsent === ACCEPT_ALL) {
          previousAcceptanceDetails = Object.keys(defaultAcceptanceDetails).reduce(
            (acc, type) => ({ ...acc, [type]: true }),
            {} as Record<CookieType, boolean>
          );
        } else {
          previousAcceptanceDetails = JSON.parse(cookieConsent);
        }

        const newAcceptanceDetails = {
          ...previousAcceptanceDetails,
          [cookieType]: accept,
        };

        this.cookieService.set(COOKIE_CONSENT, JSON.stringify(newAcceptanceDetails), {
          expires: EXPIRE_DAYS,
          path: COOKIE_PATH,
        });
      })
    )
  );

  isCookieAccepted(cookieType: CookieType) {
    const cookieContent = this.cookieService.get(COOKIE_CONSENT);

    if (cookieContent && cookieContent !== DENY_ALL) {
      if (cookieContent === ACCEPT_ALL || JSON.parse(cookieContent)?.[cookieType]) {
        return true;
      }
    }

    return false;
  }

  readonly setDisplay = this.updater((state, display: boolean) => ({
    ...state,
    display,
  }));
}
